// `cloudgrid scaffold rename <old> <new>`. Atomic rename:
//   1. Update yaml key (preserves comments + position).
//   2. `git mv services/<old> services/<new>` if inside a git repo, else
//      plain rename.
//   3. Best-effort sed rewrite of `<UPPER_OLD>_URL` references under
//      `services/*/`.
//
// Per decision 015 §"Scaffold commands". Aborts (without writing yaml) if
// the new folder already exists.

import { Command } from 'commander';
import { existsSync, renameSync } from 'node:fs';
import { join } from 'node:path';
import { spawnSync } from 'node:child_process';
import { CloudGridError } from '../../utils.js';
import { handleV2Error } from '../../handle-error.js';
import { renameService } from './yaml-edit.js';
import { rewriteSiblingUrlRefs } from './rename-refs.js';

const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/;

export interface ScaffoldRenameOpts {
  cwd?: string;
}

function isInsideGit(cwd: string): boolean {
  const r = spawnSync('git', ['rev-parse', '--is-inside-work-tree'], {
    cwd,
    stdio: 'pipe',
  });
  return r.status === 0 && r.stdout?.toString().trim() === 'true';
}

function moveServiceFolder(cwd: string, oldName: string, newName: string): void {
  const oldDir = join(cwd, 'services', oldName);
  const newDir = join(cwd, 'services', newName);
  if (isInsideGit(cwd)) {
    const r = spawnSync('git', ['mv', `services/${oldName}`, `services/${newName}`], {
      cwd,
      stdio: 'pipe',
    });
    if (r.status === 0) return;
    // Fall through to plain rename if git mv failed (e.g., file not tracked).
  }
  renameSync(oldDir, newDir);
}

export async function runScaffoldRename(
  oldName: string,
  newName: string,
  opts: ScaffoldRenameOpts = {},
): Promise<void> {
  const cwd = opts.cwd ?? process.cwd();

  if (oldName === newName) {
    throw new CloudGridError("Old and new service names are identical — nothing to rename.");
  }
  if (!SLUG_RE.test(newName)) {
    throw new CloudGridError(
      "New service name must be lowercase alphanumeric with hyphens (no leading/trailing hyphen).",
    );
  }
  if (!existsSync(join(cwd, 'cloudgrid.yaml'))) {
    throw new CloudGridError('No cloudgrid.yaml found. Run: cloudgrid init <kind> <name>');
  }

  const oldDir = join(cwd, 'services', oldName);
  const newDir = join(cwd, 'services', newName);
  if (!existsSync(oldDir)) {
    throw new CloudGridError(`services/${oldName}/ does not exist.`);
  }
  if (existsSync(newDir)) {
    throw new CloudGridError(
      `services/${newName}/ already exists. Remove it first.`,
    );
  }

  // Edit yaml first — throws if old key absent or new key already declared.
  // We do yaml before the folder rename so a yaml-side conflict aborts
  // cleanly without leaving the filesystem in a half-renamed state.
  renameService(cwd, oldName, newName);

  try {
    moveServiceFolder(cwd, oldName, newName);
  } catch (err) {
    // Try to undo the yaml rename so the user is back to a consistent state.
    try { renameService(cwd, newName, oldName); } catch { /* ignore — manual */ }
    throw new CloudGridError(
      `Failed to move services/${oldName} → services/${newName}: ${(err as Error).message}`,
    );
  }

  // Best-effort URL refs rewrite — surface what was changed.
  const summary = rewriteSiblingUrlRefs(cwd, oldName, newName);

  process.stdout.write(`Renamed service '${oldName}' → '${newName}'.\n`);
  process.stdout.write(`  yaml key updated.\n`);
  process.stdout.write(`  Folder moved: services/${oldName}/ → services/${newName}/\n`);
  if (summary.edits.length === 0) {
    process.stdout.write(`  No sibling-URL references found to rewrite.\n`);
  } else {
    process.stdout.write(`  Rewrote sibling-URL refs in ${summary.edits.length} file(s):\n`);
    for (const e of summary.edits) {
      process.stdout.write(`    ${e.path} (${e.replacements} replacement${e.replacements === 1 ? '' : 's'})\n`);
    }
  }
}

export const scaffoldRenameCommand = new Command('rename')
  .description('Rename a service: yaml key + folder + sibling-URL refs')
  .argument('<old>', 'Existing service name')
  .argument('<new>', 'New service name')
  .action(async (oldName: string, newName: string) => {
    try {
      await runScaffoldRename(oldName, newName);
    } catch (err) {
      await handleV2Error(err, { json: false });
    }
  });
